import { Clock, MapPin, Navigation } from "lucide-react";
import { useStoreConfig, isOpenNow, todayName, fmtTime } from "@/hooks/useStoreConfig";

export default function StoreInfo() {
  const config = useStoreConfig();
  if (!config?.address) return null;
  const { open, today } = isOpenNow(config.hours);
  const mapQuery = [config.location_note?.replace(/^Berlokasi di\s*/i, ""), config.address].filter(Boolean).join(", ");
  const mapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(mapQuery)}&z=16&output=embed`;

  return (
    <section id="lokasi" data-testid="store-info-section" className="py-20 bg-[#1C281C] text-[#FAF7EE] border-b border-[#2F4230]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-20 items-start mb-12">
        <div>
          <span className="text-xs uppercase tracking-widest text-[#8FA88B] font-semibold">Kunjungi Toko Kami</span>
          <h2 className="font-serif-display text-3xl sm:text-4xl font-normal mt-2 mb-6">
            Lokasi & Jam Operasional
          </h2>
          <div className="flex items-start gap-3 mb-4">
            <MapPin className="w-5 h-5 text-[#8FA88B] mt-0.5 shrink-0" />
            <div>
              <p data-testid="store-address" className="text-sm text-[#E4ECE2] leading-relaxed">{config.address}</p>
              {config.location_note && (
                <p data-testid="store-location-note" className="text-xs text-[#A8BAA5] mt-1">{config.location_note}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 mb-8">
            <Clock className="w-5 h-5 text-[#8FA88B] shrink-0" />
            <p className="text-sm text-[#E4ECE2]">
              <span
                data-testid="store-open-status"
                className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-bold mr-2 ${open ? "bg-[#3D5A40] text-[#FAF7EE]" : "bg-[#5A3D3D] text-[#F3DADA]"}`}
              >
                {open ? "Buka Sekarang" : "Tutup"}
              </span>
              {today && !today.closed && (
                <span className="text-[#CBD8C9]">
                  Hari ini {fmtTime(today.open)} – {fmtTime(today.close)}
                </span>
              )}
            </p>
          </div>
          {config.maps_url && (
            <a
              href={config.maps_url}
              target="_blank"
              rel="noreferrer"
              data-testid="store-maps-link"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FBF9F5] text-[#1E2B1D] font-semibold text-sm hover:bg-[#E9F0E8] transition-colors shadow-md"
            >
              <Navigation className="w-4 h-4" /> Buka di Google Maps
            </a>
          )}
        </div>

        <div className="bg-[#243224] rounded-3xl border border-[#2F4230] p-6 sm:p-8">
          <span className="text-xs uppercase tracking-widest text-[#8FA88B] font-semibold block mb-4">Jam Buka</span>
          <ul data-testid="store-hours-list" className="divide-y divide-[#2F4230]">
            {(config.hours || []).map((h) => {
              const isToday = h.day === todayName();
              return (
                <li
                  key={h.day}
                  data-testid={`store-hours-${h.day.toLowerCase()}`}
                  className={`flex justify-between py-2.5 text-sm ${isToday ? "text-[#FAF7EE] font-bold" : "text-[#CBD8C9]"}`}
                >
                  <span>
                    {h.day}
                    {isToday && <span className="ml-2 text-[10px] uppercase tracking-wider text-[#8FA88B]">Hari ini</span>}
                  </span>
                  <span className="font-mono">{h.closed ? "Tutup" : `${fmtTime(h.open)} – ${fmtTime(h.close)}`}</span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="rounded-3xl overflow-hidden border border-[#2F4230] shadow-2xl bg-[#243224]">
          <iframe
            title="Peta lokasi Puspita Florist"
            data-testid="store-map-embed"
            src={mapSrc}
            width="100%"
            height="380"
            style={{ border: 0, display: "block", filter: "saturate(0.85)" }}
            loading="lazy"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </section>
  );
}
